import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import {
	ICalendarIncident,
	ICmmsIncidentsQueryVariables,
	IIncident,
	IIncidentCreateResponse,
	IIncidentCreateVariables,
	IIncidentEditResponse,
	IIncidentPaginatedData,
	IIncidentPayload,
} from '../interfaces/incidents.interface';
import { IAuPaginated } from '@shared/ui-components/au-paginator/pagination-page.info';
import { map, pluck, take } from 'rxjs/operators';
import { Apollo } from 'apollo-angular';
import {
	GetIncidentQuery,
	IncidentsQuery,
} from '@app/pages/cmms/graphql/incident.queries';
import {
	CreateIncidentMutation,
	DeleteIncident,
	DeleteIncidents,
	EditIncidentMutation,
	UnlinkIncidentObjects,
} from '@app/pages/cmms/graphql/incident.mutations';
import {
	setIncidentForEditing,
	storeIncidentsQueryVariables,
} from '@app/pages/cmms/components/incident/state/incidents.actions';
import { ACTIVE_PAGE_CONFIG } from '@app/pages/cmms/configs/active-page.config';
import { CmmsPages, ICmmsState } from '@app/pages/cmms/state/cmms.reducer';
import { Store } from '@ngrx/store';
import { AuRightSidebarService } from '@core/services/au-right-sidebar.service';
import { NewIncidentComponent } from '@app/pages/cmms/components/incident/new-incident/new-incident.component';
import { iconEnum } from '@app/configs/model/IconEnum';
import { IconSnackBarService } from '@app/core/services/icon-snack-bar.service';
import { AuErrorAdapter } from '@app/shared';

@Injectable({
	providedIn: 'root',
})
export class IncidentsService {
	updateCalendarData$ = new Subject<void>();

	constructor(
		private apollo: Apollo,
		private store$: Store<ICmmsState>,
		private sidebarService: AuRightSidebarService,
		private notify: IconSnackBarService
	) {}

	loadIncidents(variables: ICmmsIncidentsQueryVariables) {
		// Due to filter and sort service are provided in component,
		// we need to store the query variables in store to use that in effects
		// when we update table after creating new item
		this.store$.dispatch(storeIncidentsQueryVariables({ variables }));

		return this.apollo
			.query<IIncidentPaginatedData>({
				query: IncidentsQuery,
				fetchPolicy: 'network-only',
				variables,
			})
			.pipe(
				pluck('data', 'incidents'),
				map(incidents => this.getIncidentsData(incidents))
			);
	}

	loadCalendarIncidents(
		variables: ICmmsIncidentsQueryVariables
	): Observable<ICalendarIncident[]> {
		return this.apollo
			.query<{ incidents: { edges: { node: ICalendarIncident }[] } }>({
				query: IncidentsQuery,
				fetchPolicy: 'network-only',
				variables: {
					...variables,
					isFullPaginatedFragmentQuery: false,
					isCalendarQuery: true,
				},
			})
			.pipe(map(res => res.data['incidents'].edges.map(edge => edge.node)));
	}

	getIncidentsData(paginatedIncidents: IAuPaginated<IIncident>) {
		const incidents = paginatedIncidents.edges.map(edge => edge.node);

		return {
			incidents,
			incidentPagination: {
				pageInfo: paginatedIncidents.pageInfo,
				totalCount: paginatedIncidents.totalCount,
			},
		};
	}

	createIncidents(variables: IIncidentCreateVariables) {
		return this.apollo
			.mutate<IIncidentCreateResponse>({
				mutation: CreateIncidentMutation,
				variables: variables,
			})
			.pipe(pluck('data', 'createIncident'));
	}

	editIncident(
		variables: Partial<IIncidentPayload>,
		unlink?: { pk: string; workOrders?: string; requests?: string }[]
	) {
		unlink?.map(remove => {
			this.unlinkAttachedElements(remove).subscribe({
				error: error => {
					this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
				},
			});
		});

		return this.apollo
			.mutate<IIncidentEditResponse>({
				mutation: EditIncidentMutation,
				variables,
			})
			.pipe(pluck('data', 'editIncident'));
	}

	deleteIncident(pk: string) {
		return this.apollo
			.mutate<{ success: boolean }>({
				mutation: DeleteIncident,
				variables: {
					pk,
				},
			})
			.pipe(pluck('data', 'deleteIncident'));
	}

	deleteIncidents(pks: string[]) {
		return this.apollo.mutate({
			mutation: DeleteIncidents,
			variables: {
				incidents: pks,
			},
		});
	}

	getIncidentByPk(pk: string) {
		return this.apollo
			.query<{ incident: IIncident }>({
				query: GetIncidentQuery,
				fetchPolicy: 'network-only',
				variables: {
					id: pk,
				},
			})
			.pipe(pluck('data', 'incident'));
	}

	unlinkAttachedElements(variables: {
		pk: string;
		workOrders?: string;
		requests?: string;
	}) {
		return this.apollo
			.mutate<{ success: boolean }>({
				mutation: UnlinkIncidentObjects,
				fetchPolicy: 'network-only',
				variables,
			})
			.pipe(take(1), pluck('data'));
	}

	viewIncidentInWorkspace(incident: IIncident): void {
		this.store$.dispatch(setIncidentForEditing({ incidentEdit: incident }));
		this.sidebarService.setComponentAndOpen(
			ACTIVE_PAGE_CONFIG[CmmsPages.editIncident].createComponent,
			{
				...ACTIVE_PAGE_CONFIG[CmmsPages.editIncident].sidebarConfig,
				title: { text: 'incident_id', endfix: incident.incidentId },
			}
		);
	}

	openNewIncidentWorkspace() {
		this.sidebarService.setComponentAndOpen(NewIncidentComponent, {
			title: { prefix: 'new_text', text: 'incident' },
			icon: iconEnum.incident,
			actionBtnName: 'submit',
			propagateCloseEvent: true,
			showContentActions: true,
		});
	}
}
